import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { ApiError } from "../api.ts";
import { useBrand, useMe, usePublicConfig, useSettings } from "../hooks.ts";
import { useSaveSettings } from "../pages/Settings.tsx";
import { browserZone } from "../lib/cron.ts";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePendingApprovals } from "../pages/Approvals.tsx";
import { cx, Spinner } from "./ui.tsx";
import { Icon } from "./icons.tsx";
import { MenuLogo, Sidebar } from "./Sidebar.tsx";
import { ImpersonationBanner } from "./impersonation.tsx";

/** The logo sits on the dark sidebar colour: Mnemio's white logo, the install's own, or its name. */
export function Logo({ className = "h-6" }: { className?: string }) {
  const b = useBrand();
  if (b.loading) return <span className={cx("inline-block", className)} />;
  if (b.logoUrl) return <img src={b.logoUrl} alt={b.name} className={cx("w-auto max-w-full select-none object-contain", className)} draggable={false} />;
  if (b.custom) return <span className={cx("flex items-center text-lg font-semibold tracking-tight text-white", className)}>{b.name}</span>;
  return <img src="/mnemio-logo.svg" alt={b.name} className={cx("w-auto select-none", className)} draggable={false} />;
}

/** Keeps the tab title, icon and colours in step with Admin → Branding (also after a save). */
export function BrandEffects() {
  const b = useBrand();
  useEffect(() => {
    if (b.loading) return;
    document.title = b.name;
    const root = document.documentElement.style;
    root.setProperty("--color-accent", b.accentColor);
    root.setProperty("--chrome", b.sidebarColor);
    root.setProperty("--chrome-2", `color-mix(in srgb, ${b.sidebarColor} 88%, white)`);
    for (const rel of ["icon", "apple-touch-icon"]) {
      const link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (link) {
        link.removeAttribute("type");
        link.href = b.faviconUrl;
      }
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", b.sidebarColor);
  }, [b.loading, b.name, b.accentColor, b.sidebarColor, b.faviconUrl]);
  return null;
}

/**
 * Until someone picks a time zone, their assistant takes this browser's (once, on their own sign-in —
 * never while an admin is viewing as them). They can change it in Settings.
 */
function useAdoptBrowserZone(enabled: boolean) {
  const settings = useSettings();
  const save = useSaveSettings();
  const tried = useRef(false);
  useEffect(() => {
    if (!enabled || tried.current || !settings.data || settings.data.timezone !== null) return;
    tried.current = true;
    save.mutate({ timezone: browserZone() });
  }, [enabled, settings.data, save]);
}

export type ShellContext = { setChatBusy: (busy: boolean) => void };
const COLLAPSE_KEY = "mnemio.menu";

export function Shell() {
  const me = useMe();
  const cfg = usePublicConfig();
  useAdoptBrowserZone(!!me.data && !me.data.user.impersonatedBy);
  const approvals = usePendingApprovals();
  const loc = useLocation();
  const navigate = useNavigate();
  // Phones and tablets: the menu opens over the page from the top bar, and closes on the next page.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  // Desktop: the menu folds to a rail of icons, remembered on this browser.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "collapsed";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "open" : "collapsed");
      } catch {
        /* private window: just this visit */
      }
      return !c;
    });
  const [chatBusy, setChatBusy] = useState(false);
  if (me.isPending) return <div className="grid h-full place-items-center"><Spinner /></div>;
  // Remember where they were going (e.g. a /link/… page from a chat app) and return after sign-in.
  if (me.error instanceof ApiError && me.error.status === 401)
    return <Navigate to={loc.pathname === "/" ? "/login" : `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (me.error) return <div className="grid h-full place-items-center text-sm text-[var(--muted)]">{me.error.message}</div>;
  const { user } = me.data;
  const imp = user.impersonatedBy;
  // Admin pages are the admin's own, never while acting as someone (even another admin): the server refuses them too.
  const admin = user.role === "admin" && !imp;
  if (loc.pathname.startsWith("/admin") && !admin) return <Navigate to="/" replace />;
  // Web apps: when the install has an apps domain and the group may publish them (they're built in the terminal).
  const perms = me.data.group?.permissions;
  const webApps = !!cfg.data?.appsDomain && perms?.webApps !== false && perms?.terminal !== false;
  // Chat is a full-height app surface; every other page is a padded, centred document.
  const fullBleed = loc.pathname.startsWith("/chat");
  const pending = approvals.data?.pending ?? 0;
  const menu = { user, admin, webApps, pending, busy: chatBusy };

  return (
    <>
    {imp && <ImpersonationBanner asUser={{ name: user.name, email: user.email }} mode={imp.mode} adminEmail={imp.email} expiresAt={imp.expiresAt} />}
    <div className={cx("flex flex-col lg:flex-row", fullBleed ? (imp ? "h-[calc(100dvh-2.5rem)]" : "h-dvh") : "min-h-full")}>
      {/* Phones and tablets: menu on the left, new chat on the right (as in the ChatGPT and Claude apps). */}
      <header className={cx("chrome-glow sticky z-30 flex h-14 shrink-0 items-center gap-1 bg-[var(--chrome)] px-2 lg:hidden", imp ? "top-10" : "top-0")}>
        <button aria-label="Open menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)} className="relative grid h-10 w-10 place-items-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white">
          <Icon name="menu" size={20} />
          {pending > 0 && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[var(--color-brand)] ring-2 ring-[var(--chrome)]" />}
        </button>
        <NavLink to="/chat" className="flex min-w-0 items-center px-1"><MenuLogo /></NavLink>
        <button aria-label="New chat" disabled={chatBusy} onClick={() => navigate("/chat")} className="ml-auto grid h-10 w-10 place-items-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white disabled:opacity-50">
          <Icon name="newChat" size={19} />
        </button>
      </header>
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)}>
        <Sidebar {...menu} collapsed={false} onClose={() => setMenuOpen(false)} />
      </Drawer>

      <aside className={cx("chrome-glow hidden shrink-0 bg-[var(--chrome)] transition-[width] duration-200 lg:sticky lg:block", collapsed ? "w-[60px]" : "w-[260px]", imp ? "lg:top-10 lg:h-[calc(100vh-2.5rem)]" : "lg:top-0 lg:h-screen")}>
        <Sidebar {...menu} collapsed={collapsed} onCollapse={toggle} />
      </aside>
      {fullBleed ? (
        <main className="min-h-0 min-w-0 flex-1"><Outlet context={{ setChatBusy } satisfies ShellContext} /></main>
      ) : (
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-12 lg:py-11">
          <div className="mx-auto max-w-5xl"><Outlet context={{ setChatBusy } satisfies ShellContext} /></div>
        </main>
      )}
    </div>
    </>
  );
}

/** The phone/tablet menu: the sidebar's contents in a panel over the page. Esc or a tap outside closes it. */
function Drawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", esc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  return (
    <div className={cx("fixed inset-0 z-50 lg:hidden", !open && "pointer-events-none")} aria-hidden={!open}>
      <div onClick={onClose} className={cx("absolute inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-200", open ? "opacity-100" : "opacity-0")} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className={cx("chrome-glow absolute inset-y-0 left-0 w-[min(18rem,85vw)] bg-[var(--chrome)] transition-transform duration-200 ease-out", open ? "translate-x-0 shadow-2xl" : "-translate-x-full")}
      >
        {open && children}
      </div>
    </div>
  );
}
